// src/components/Header.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import SlideMenuPanel from "components/menus/SlideMenuPanel";
import { useMe } from "contexts/MeContext";

type Props = {
  onMenuClick?: () => void;
  /** LP側などから渡す実データ。将来用に残すが、基本は MeContext を使う */
  userId?: string;
  userName?: string;
  points?: number;
};

const LINE_FRIEND_URL = "https://lin.ee/EDKV4tY";

const Header: React.FC<Props> = ({ onMenuClick, userId, userName, points }) => {
  const [open, setOpen] = useState(false);
  const { me } = useMe();

  // MeContext（/api/auth/me）＋ props から会員情報を決定
  const anyMe = me as any;

  const rawUserId: string | undefined =
    userId ??
    (me?.id != null
      ? String(me.id)
      : anyMe?.userId != null
      ? String(anyMe.userId)
      : undefined);

  const rawUserName: string | undefined =
    (userName ?? (me?.lineName || anyMe?.name || "")).trim() || undefined;

  const rawPoints: number | string | undefined =
    typeof points !== "undefined"
      ? points
      : typeof me?.points !== "undefined"
      ? me.points
      : anyMe?.purchasePoint;

  const toggle = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      setOpen((v) => !v);
      onMenuClick?.();
    },
    [onMenuClick]
  );

  const close = useCallback(() => setOpen(false), []);

  // メニュー開時に背景スクロールを止める（iOS含む）
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (open) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // 表示用整形
  // 数値IDならそのまま表示（例: id=51000001 → 51000001）
  const displayUserId =
    rawUserId && /^\d+$/.test(rawUserId) ? rawUserId : "—";

  const displayPoints =
    typeof rawPoints === "number"
      ? rawPoints.toLocaleString()
      : typeof rawPoints === "string" && rawPoints !== ""
      ? Number(rawPoints).toLocaleString()
      : "—";

  // gap を PC=6px / SP=4px（既存維持）
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ログイン判定
  const isLoggedIn = !!rawUserId;

  // 友だち追加ボタン表示条件：
  // - ログイン済み
  // - friendBonusClaimed が true でない（undefined のときは表示）
  const friendBonusClaimed =
    typeof anyMe?.friendBonusClaimed === "boolean"
      ? anyMe.friendBonusClaimed
      : false;

  const showFriendBonusButton = isLoggedIn && !friendBonusClaimed;

  return (
    <>
      <header
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 720,
          margin: "0 auto",
          background: "transparent",
          padding: "10px 10px 0",
          position: "sticky",
          top: 0,
          zIndex: 1000,
          pointerEvents: "auto",
        }}
      >
        {/* === 上段：ロゴ／ユーザープレート／メニュー === */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            alignItems: "center",
            columnGap: 10,
            position: "relative",
            zIndex: 1000,
          }}
        >
          {/* 左上ロゴ（現状維持：高さ65px） */}
          <Link
            to="/"
            aria-label="HOME"
            onClick={(e) => e.stopPropagation()}
            style={{ display: "inline-block", position: "relative", zIndex: 1000 }}
          >
            <img
              src="/assets/member/LOGO_result.webp"
              alt="BOAT億MAN"
              style={{ height: 65, width: "auto", display: "block", transform: "translateY(2px)" }}
              loading="eager"
            />
          </Link>

          {/* ▼ ブルー長方形プレート */}
          <div
            aria-label="ユーザー情報"
            style={{
              justifySelf: "start",
              position: "relative",
              zIndex: 1000,
              width: "100%",
              maxWidth: 280,
            }}
          >
            <style>{`
              .userPlateDual {
                display: grid;
                grid-template-rows: auto auto;
                background: linear-gradient(180deg, #0a1e3a, #113b6b);
                border: 2px solid #cde0ff;
                border-radius: 10px;
                color: #fff;
                padding: 6px 10px;
                width: 100%;
                box-shadow: 0 2px 6px rgba(0,0,0,0.35);
                font-weight: 700;
                line-height: 1.3;
                white-space: nowrap;
              }
              .userPlateDual__top {
                display: grid;
                grid-template-columns: 1fr 1fr;
                align-items: center;
                font-size: 13px;
              }
              .userPlateDual__top span:first-child { textAlign: left;  opacity: 0.9; }
              .userPlateDual__top span:last-child  { textAlign: right; }
              .userPlateDual__bottom {
                display: grid;
                grid-template-columns: 1fr 1fr;
                align-items: center;
                margin-top: 3px;
                font-size: 12.5px;
                border-top: 1px solid rgba(255,255,255,0.2);
                padding-top: 3px;
                column-gap: 10px;
              }
              .userPlateDual__col {
                display: grid;
                grid-template-columns: 1fr 1fr;
                align-items: center;
              }
              .userPlateDual__col span:first-child { textAlign: left;  opacity: 0.9; }
              .userPlateDual__col span:last-child  { textAlign: right; }
              .userPlateDual__col--points { grid-template-columns: 1fr 1fr 0.6fr; }
              .userPlateDual__col--points span:nth-child(1) { textAlign: left;  opacity: 0.9; }
              .userPlateDual__col--points span:nth-child(2) { textAlign: center; }
              .userPlateDual__col--points span:nth-child(3) { textAlign: right; }
              @media (max-width: 480px) {
                .userPlateDual { padding: 6px 8px; }
              }
            `}</style>

            <div className="userPlateDual">
              <div className="userPlateDual__top">
                <span>Username</span>
                <span>{rawUserName || "—"}</span>
              </div>

              <div className="userPlateDual__bottom">
                {/* 左：ID */}
                <div className="userPlateDual__col">
                  <span>ID：</span>
                  <span>{displayUserId}</span>
                </div>

                {/* 右：保有 */}
                <div className="userPlateDual__col userPlateDual__col--points">
                  <span>保有：</span>
                  <span>{displayPoints}</span>
                  <span>pt</span>
                </div>
              </div>
            </div>
          </div>

          {/* ハンバーガー */}
          <button
            onClick={toggle}
            aria-label="メニューを開く"
            style={{
              width: 60,
              height: 56,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 10px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              position: "relative",
              zIndex: 1001,
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <span style={{ width: 40, height: 6, background: "#0e1b26", borderRadius: 4 }} />
            <span style={{ width: 40, height: 6, background: "#0e1b26", borderRadius: 4 }} />
            <span style={{ width: 40, height: 6, background: "#0e1b26", borderRadius: 4 }} />
          </button>
        </div>

        {/* === 中段：黒ボタン群 === */}
        <nav
          aria-label="メインメニュー"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: isMobile ? 4 : 8,
            margin: "12px 0 8px",
            padding: "0 2px",
            position: "relative",
            zIndex: 1000,
            pointerEvents: "auto",
          }}
        >
          {[
            { to: "/",           alt: "HOME",         img: "menu-01.webp" },
            { to: "/mypage",     alt: "マイページ",   img: "menu-02.webp" },
            { to: "/plans",      alt: "プラン一覧",   img: "menu-03.webp" },
            { to: "/results",    alt: "実績",         img: "menu-04.webp" },
            { to: "/points-buy", alt: "ポイント購入", img: "menu-05.webp" },
            { to: "/reviews",    alt: "レビュー",     img: "menu-06.webp" },
            { to: "/notices",    alt: "お知らせ",     img: "menu-07.webp" },
            { to: "/contact",    alt: "お問い合わせ", img: "menu-08.webp" },
          ].map(({ to, alt, img }) => (
            <Link key={to} to={to} aria-label={alt} style={{ display: "block", overflow: "visible" }}>
              <img
                src={`/assets/member/${img}`}
                alt={alt}
                style={{
                  width: "100%",
                  display: "block",
                  transform: "scale(1.03)",
                  transformOrigin: "center",
                  willChange: "transform",
                }}
                loading="lazy"
              />
            </Link>
          ))}
        </nav>

        {/* === 友だち追加で50pt GET（金ボタン） === */}
        {showFriendBonusButton && (
          <div
            style={{
              padding: "0 4px 8px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <a
              href={LINE_FRIEND_URL}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-block",
                width: "100%",
                maxWidth: 720,
                textDecoration: "none",
              }}
            >
              <button
                type="button"
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: 999,
                  padding: "10px 12px",
                  fontSize: 16,
                  fontWeight: 900,
                  letterSpacing: "0.06em",
                  cursor: "pointer",
                  background:
                    "linear-gradient(90deg, #f7d36b, #f1b93b, #f7d36b)",
                  color: "#5a3b00",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
                  textShadow: "0 1px 1px rgba(255,255,255,0.6)",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                🎁 友だち追加で50pt GET
              </button>
            </a>
          </div>
        )}
      </header>

      {/* === 固定配置のスライドメニュー === */}
      <SlideMenuPanel open={open} onClose={close} />
    </>
  );
};

export default Header;
